import type { Dispatch, RefObject, SetStateAction } from "react"
import { useRef, useState } from "react"
import { msg, t } from "@lingui/core/macro"
import { ConfirmModal } from "../../common/molecules/ConfirmModal"
import { Alignment } from "../../common/molecules/ConfirmModal/ConfirmModal"
import { useMessagingView } from "../hooks"
import UserFeedback, {
    DEFAULT_FADE_MS,
    isDefaultFormProps,
} from "../UserFeedback/UserFeedback"
import styles from "./Inbox.module.scss"
import type { FeedbackRef, FormProps } from "../UserFeedback/UserFeedback"

const CONFIRMATION_TITLE = msg`Are you sure?`
const CONFIRMATION_MESSAGE = msg`You have unsaved changes. If you leave this page, your feedback will not be saved.`
const CONFIRMATION_YES = msg`Yes, leave`
const CONFIRMATION_CANCEL = msg`Cancel`

/*
 * Defines the necessary props and functions to use for operating the User Feedback workflow.
 */
interface Flow {
    /*
     * If the user initiated the User Feedback request flow or not
     */
    isUserInitiated: boolean
    /*
     * If this will manage the closing of the User Feedback component or not.
     */
    override: boolean
    /*
     * The method that will be triggered when the User Feedback component is being closed.
     * The `override` method above may control the timing of the actual closing.
     */
    handleFeedbackClosing: (submitted: boolean, props?: FormProps) => void
    /*
     * The method that will be triggered when cancel on the Confirmation component is clicked.
     */
    handleCancelConfirmation: () => void
    /*
     * The method that will be triggered when accept on the Confirmation component is clicked.
     */
    handleAcceptConfirmation: () => void
}

/*
 * Defines the inputs used to help generate the `Flow` objects.
 * Not all the attributes will be used by all the factories.
 */
type FlowFactory = (
    /*
     * Reference to the Feedback component. Used to possibly trigger the close method
     * which animates the dismissal.
     */
    feedbackRef: RefObject<FeedbackRef>,
    /*
     * Hook to set the data used by the form. If the form needs to be re-rendered
     * from scratch, this will save the props for it.
     */
    setFormProps: Dispatch<SetStateAction<FormProps | undefined>>,
    /*
     * Hook to set the displaying of the Confirmation component.
     */
    setShowConfirmModal: Dispatch<SetStateAction<boolean>>,
    /*
     * Hook to set the displaying of the Feedback component.
     */
    setShowFeedback: Dispatch<SetStateAction<boolean>>,
) => Flow

/*
 * Factory to generate the `Flow` interface for a system initiated feedback in mobile.
 */
const systemMobileFlow: FlowFactory = (
    childRef: any,
    setFormProps: any,
    setShowConfirmModal: any,
    setShowFeedback: any,
): Flow => {
    return {
        isUserInitiated: false,
        override: false,
        handleFeedbackClosing: (submitted: boolean, props?: FormProps) => {
            const fade = childRef?.current?.getCloseTimeout() || DEFAULT_FADE_MS
            childRef?.current?.onClose()

            setTimeout(() => {
                setShowFeedback(false)
            }, fade)
        },
        handleCancelConfirmation: () => {
            setShowConfirmModal(false)
            setShowFeedback(true)
        },
        handleAcceptConfirmation: () => {
            setFormProps(undefined)
            setShowConfirmModal(false)
            setShowFeedback(false)
        },
    }
}

/*
 * Factory to generate the `Flow` interface for a user initiated feedback in mobile.
 */
const userMobileFlow: FlowFactory = (
    childRef: any,
    setFormProps: any,
    setShowConfirmModal: any,
    setShowFeedback: any,
): Flow => {
    return {
        isUserInitiated: true,
        override: false,
        handleFeedbackClosing: (submitted: boolean, props?: FormProps) => {
            setShowFeedback(false)
        },
        handleCancelConfirmation: () => {
            setShowConfirmModal(false)
            setShowFeedback(true)
        },
        handleAcceptConfirmation: () => {
            setFormProps(undefined)
            setShowConfirmModal(false)
            setShowFeedback(false)
        },
    }
}

/*
 * Factory to generate the `Flow` interface for a user initiated feedback in desktop.
 */
const userDesktopFlow: FlowFactory = (
    childRef: any,
    setFormProps: any,
    setShowConfirmModal: any,
    setShowFeedback: any,
): Flow => {
    return {
        override: true,
        isUserInitiated: true,
        handleFeedbackClosing: (submitted: boolean, props?: FormProps) => {
            if (submitted) {
                setFormProps(undefined)
                setShowFeedback(false)
            } else if (!isDefaultFormProps(props)) {
                setShowFeedback(false)
                setShowConfirmModal(true)
                setFormProps(props)
            } else {
                setShowFeedback(false)
            }
        },
        handleCancelConfirmation: () => {
            setShowConfirmModal(false)
            setShowFeedback(true)
        },
        handleAcceptConfirmation: () => {
            setFormProps(undefined)
            setShowConfirmModal(false)
            setShowFeedback(false)
        },
    }
}

/*
 * Factory to generate the `Flow` interface for a system initiated feedback in desktop.
 */
const systemDesktopFlow: FlowFactory = (
    childRef: any,
    setFormProps: any,
    setShowConfirmModal: any,
    setShowFeedback: any,
): Flow => {
    return {
        isUserInitiated: false,
        override: true,
        handleFeedbackClosing: (submitted: boolean, props?: FormProps) => {
            if (submitted) {
                const fade =
                    childRef?.current?.getCloseTimeout() || DEFAULT_FADE_MS
                setFormProps(undefined)
                setTimeout(() => {
                    setShowFeedback(false)
                }, fade)
            } else if (!isDefaultFormProps(props)) {
                setShowConfirmModal(true)
                setFormProps(props)
            } else {
                const fade =
                    childRef?.current?.getCloseTimeout() || DEFAULT_FADE_MS
                childRef?.current?.onClose()

                setTimeout(() => {
                    setShowFeedback(false)
                }, fade)
            }
        },
        handleCancelConfirmation: () => {
            setShowConfirmModal(false)
            setShowFeedback(true)
        },
        handleAcceptConfirmation: () => {
            setFormProps(undefined)
            setShowConfirmModal(false)

            const fade = childRef?.current?.getCloseTimeout() || DEFAULT_FADE_MS
            childRef?.current?.onClose()
            setTimeout(() => {
                setShowFeedback(false)
            }, fade)
        },
    }
}

/*
 * Helper to extract out complicated logic from the Inbox. This handles the workflows for
 * both Mobile vs Desktop and User vs System initiated interactions.
 */
export const FeedbackHelper = ({
    showFeedback,
    setShowFeedback,
    userClickedFeedbackIcon,
}: {
    showFeedback: boolean
    setShowFeedback: Dispatch<SetStateAction<boolean>>
    userClickedFeedbackIcon: boolean
}) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [formProps, setFormProps] = useState<FormProps>()
    const feedbackRef = useRef<FeedbackRef>(null)
    const messagingView = useMessagingView()

    let factory: FlowFactory
    if (userClickedFeedbackIcon && messagingView.isMobile) {
        factory = userMobileFlow
    } else if (userClickedFeedbackIcon && messagingView.isDesktopInbox) {
        factory = userDesktopFlow
    } else if (!userClickedFeedbackIcon && messagingView.isMobile) {
        factory = systemMobileFlow
    } else if (!userClickedFeedbackIcon && messagingView.isDesktopInbox) {
        factory = systemDesktopFlow
    } else {
        // Default to User Mobile style
        factory = userMobileFlow
    }
    const flow: Flow = factory(
        feedbackRef,
        setFormProps,
        setShowConfirmModal,
        setShowFeedback,
    )

    return (
        <>
            {showFeedback && (
                <UserFeedback
                    ref={feedbackRef}
                    initialRating={formProps?.rating}
                    initialComments={formProps?.comments}
                    onClose={flow.handleFeedbackClosing}
                    isUserInitiated={flow.isUserInitiated}
                    handleClose={flow.override}
                />
            )}
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => flow.handleCancelConfirmation()}
                onConfirm={() => flow.handleAcceptConfirmation()}
                textAlign={Alignment.left}
                title={t(CONFIRMATION_TITLE)}
                message={t(CONFIRMATION_MESSAGE)}
                confirmText={t(CONFIRMATION_YES)}
                cancelText={t(CONFIRMATION_CANCEL)}
                titleClass={styles.confirmationTitle}
                messageClass={styles.confirmationMessage}
            />
        </>
    )
}
